import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  format,
} from 'date-fns';
import { Stack, Typography } from '@mui/material';
import { BookmarkBorder, Search } from '@mui/icons-material';
import {
  BasketAddRemoveEvent,
  BasketCheckoutEvent,
  CategoryViewShopperEvent,
  ProductViewShopperEvent,
  SearchShopperEvent,
  ShopperEvent,
  ShopperEventType,
} from '@vs-online-only-app/state';
import { ProductCard, ThumbnailSize } from '@vs-online-only-app/ui';

const SearchOrCategoryViewEventCard = styled(Stack)<{ $large?: boolean }>`
  align-items: center;
  justify-content: center;
  width: ${({ $large }) => ($large ? '140px' : '96px')};
  height: ${({ $large }) => ($large ? '140px' : '96px')};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const formatEventTimestamp = (timestamp: string) => {
  const currentTs = Date.now();
  const tsDate = new Date(timestamp);
  const daysDiff = differenceInCalendarDays(currentTs, tsDate);
  const minsDiff = differenceInMinutes(currentTs, tsDate);
  const hoursDiff = differenceInHours(currentTs, tsDate);
  if (minsDiff < 1) {
    return 'Just now';
  }
  if (minsDiff < 60) {
    return `${minsDiff} m ago`;
  }
  if (daysDiff < 1) {
    return `${hoursDiff} h ago`;
  }
  if (daysDiff < 2) {
    return 'Yesterday';
  }
  if (daysDiff <= 30) {
    return `${daysDiff} days ago`;
  }

  return format(tsDate, 'd MMM, yyyy');
};

type Props = {
  event: ShopperEvent;
  large?: boolean;
  thumbnailOnly?: boolean;
  noTimestamp?: boolean;
  onClick?: () => void;
};

export const ShopperEventsListItem: FC<Props> = ({
  event,
  large,
  thumbnailOnly,
  noTimestamp,
}) => {
  const [formattedEventTimestamp, setFormattedEventTimestamp] = useState<
    string | undefined
  >(event.time && !noTimestamp ? formatEventTimestamp(event.time) : undefined);

  useEffect(() => {
    if (!event.time || noTimestamp) {
      return;
    }
    const updateDate = () =>
      setFormattedEventTimestamp(formatEventTimestamp(event.time));
    const interval = setInterval(updateDate, 30 * 1000);
    return () => clearInterval(interval);
  }, [event.time, noTimestamp]);

  const renderProductViewedEvent = (event: ProductViewShopperEvent) => (
    <ProductCard
      product={{
        ...event.data.product,
        thumbnail: event.data.product.images?.[0],
      }}
      thumbnailOnly
      thumbnailSize={large ? ThumbnailSize.ExtraLarge : ThumbnailSize.Large}
      noPadding
      key={event.id}
    />
  );

  const renderSearchOrCategoryViewEvent = (
    event: SearchShopperEvent | CategoryViewShopperEvent,
  ) => (
    <SearchOrCategoryViewEventCard $large={large} key={event.id}>
      {event.type === ShopperEventType.CategoryView && (
        <BookmarkBorder color="disabled" />
      )}
      {event.type === ShopperEventType.Search && <Search color="disabled" />}
      <Typography color="textSecondary" variant="caption" align="center">
        {event.data.value}
      </Typography>
    </SearchOrCategoryViewEventCard>
  );

  const renderBasketAddRemoveEvent = (event: BasketAddRemoveEvent) => (
    <ProductCard
      product={{
        ...event.data.basket.product,
        thumbnail: event.data.basket.product.images?.[0],
      }}
      thumbnailOnly={thumbnailOnly}
      thumbnailSize={large ? ThumbnailSize.Regular : ThumbnailSize.Small}
      long={Boolean(large)}
      key={event.id}
    />
  );

  const renderBasketCheckoutEvent = (event: BasketCheckoutEvent) => (
    <>
      {event.data.transaction.line_items.map((item) => (
        <ProductCard
          thumbnailOnly={thumbnailOnly}
          product={{
            ...item.product,
            thumbnail: item.product.images?.[0],
          }}
          thumbnailSize={large ? ThumbnailSize.Regular : ThumbnailSize.Small}
          long={Boolean(large)}
          key={item.product.url}
        />
      ))}
    </>
  );

  return (
    <Stack gap={0.5}>
      {event.type === ShopperEventType.ProductView &&
        renderProductViewedEvent(event)}
      {(event.type === ShopperEventType.BasketAdd ||
        event.type === ShopperEventType.BasketDelete) &&
        renderBasketAddRemoveEvent(event)}
      {(event.type === ShopperEventType.CategoryView ||
        event.type === ShopperEventType.Search) &&
        renderSearchOrCategoryViewEvent(event)}
      {event.type === ShopperEventType.BasketCheckout &&
        renderBasketCheckoutEvent(event)}
      {formattedEventTimestamp && (
        <Typography variant="caption" color="textSecondary">
          {formattedEventTimestamp}
        </Typography>
      )}
    </Stack>
  );
};
